const regularOrder = ['XS','S','M','L','XL','2X','3X','4X','5X','6X'];
const bigOrder = ['1XB','LT','XLT','2XB','2XLT','3XB','3XLT','4XB','4XLT','5XB','5XLT','6XB','6XLT','1XLT','MT','2LT','3LT','4LT'];
const cache = new Map();
const reviewMode = typeof document !== 'undefined' && document.documentElement.hasAttribute('data-us-review');
const pageURL = () => new URL(reviewMode ? window.parent.location.href : location.href);
const pageHistory = () => reviewMode ? window.parent.history : history;
const storeOrigin = () => new URL(document.baseURI).origin;

export function normalizeSize(value) {
  const raw = String(value || '').trim();
  if (/\+|\b(?:jacket|joggers|pants|sweatshirt)\b/i.test(raw)) return raw;
  const declared = raw.split('(')[0].trim().toUpperCase().replace(/[\s-]/g, '');
  const names = {EXTRASMALL:'XS',XSMALL:'XS',SMALL:'S',MEDIUM:'M',LARGE:'L',XLARGE:'XL',EXTRALARGE:'XL',XXL:'2X',XXLARGE:'2X',XXXL:'3X',XXXLARGE:'3X',XXXXL:'4X',XXXXXL:'5X',XXXXXXL:'6X'};
  if (names[declared]) return names[declared];
  if (/^[2-9]XL?$/.test(declared)) return declared.replace(/L$/, '');
  if (/^[1-9]XBIG$/.test(declared)) return declared.replace('BIG', 'B');
  if (/^[1-9]XTALL$/.test(declared)) return declared.replace('TALL', 'LT');
  if (/^(?:XS|S|M|L|XL|[1-9]XB|[1-9]XLT|[1-9]LT|MT|LT|XLT)$/.test(declared)) return declared;
  return raw;
}

export function sizeGroup(size) {
  if (regularOrder.includes(size)) return 'regular';
  if (/^(?:[1-9]XB|[1-9]XLT|[1-9]LT|MT|LT|XLT)$/.test(size) || /BIG|TALL|\dB\b/i.test(size)) return 'big';
  if (/^\d/.test(size)) return 'numeric';
  return 'other';
}

export function matchingVariants(product, sizes) {
  return product.variants.filter(variant => sizes.has(normalizeSize(variant.size)));
}

export function styleMatches(product, style) {
  if(style === 'all') return true;
  const title=product.title || '', type=(product.type || '').toLowerCase(), collections=product.collections || [];
  const inCollection=(...handles)=>handles.some(handle=>collections.includes(handle));
  const sweatshirt=/\bsweatshirts?\b/i.test(title);
  const sweater=/\bsweaters?|cardigans?\b/i.test(title) && !sweatshirt;
  const hoodie=/\bhoodies?|hooded\b/i.test(title);
  const jacket=/\b(?:jacket|coat|parka)\b(?!.*\b(?:hoodie|sweater|sweatshirt)\b)/i.test(title);
  const tee=/\b(?:t[ -]?shirts?|tees?)\b/i.test(title);
  const suit=/\bsweatsuits?|track[ -]?suits?\b|\bmatching (?:joggers|pants)|\b(?:jogger|fleece|hoodie|track) sets?\b/i.test(title);
  if(style==='rugby') return /\brugby\b/i.test(title+' '+type);
  if(style==='sweatshirts') return sweatshirt && !hoodie && !suit;
  if(style==='pants-jeans') return inCollection('pants','jeans');
  if(style==='sweatsuits') return inCollection('sweatsuit','sweatsuits');
  if(style==='hoodies') return inCollection('hoodies') && !sweater && !jacket && !tee && !suit;
  if(style==='sweaters') return inCollection('sweaters') && !sweatshirt && !(hoodie && !sweater) && !jacket;
  if(style==='jackets') return inCollection('jackets') && !suit && !sweatshirt && !sweater;
  if(style==='t-shirts') return inCollection('t-shirts','t-shirts-1') && !hoodie && !jacket && !sweatshirt && !sweater && !suit;
  return inCollection(style);
}

async function loadCollection(root, style) {
  const handle = ['rugby','sweatshirts'].includes(style) ? 'all' : style;
  const key = `${root.dataset.theme}:${handle}`;
  const stored = cache.get(key);
  if (stored && Date.now() - stored.time < 60000) return stored.promise;
  const promise = (async () => {
    async function page(number) {
      const url = new URL(`${root.dataset.root}collections/${encodeURIComponent(handle)}`, storeOrigin());
      url.searchParams.set('view', 'us-size-data');
      url.searchParams.set('page', String(number));
      const response = await fetch(url, {credentials:'same-origin', cache:'no-store', headers:{Accept:'text/html'}});
      if (!response.ok) throw new Error('Availability request failed');
      const data = await response.json();
      if (String(data.theme) !== root.dataset.theme || data.page !== number || !Array.isArray(data.products)) throw new Error('Unexpected availability response');
      return data;
    }
    const first = await page(1);
    if (first.pages > 100) throw new Error('Collection exceeds verified pagination limit');
    const products = [...first.products];
    for (let next = 2; next <= first.pages; next += 4) {
      const pages = await Promise.all(Array.from({length:Math.min(4, first.pages-next+1)}, (_, i) => page(next+i)));
      pages.forEach(data => products.push(...data.products));
    }
    return [...new Map(products.map(product => [product.id, product])).values()];
  })();
  cache.set(key, {time:Date.now(), promise});
  try { return await promise; } catch (error) { cache.delete(key); throw error; }
}

function init(root) {
  if(root.dataset.ready) return;
  root.dataset.ready='true';
  const collection=root.dataset.mode==='collection', nativeStyle=(root.dataset.collection || 'all').replace(/^sweatsuit$/, 'sweatsuits');
  const words=JSON.parse(root.querySelector('[data-us-words]').innerHTML);
  const help=root.querySelector('.us-help'), status=root.querySelector('.us-status'), grid=root.querySelector('.us-results'), more=root.querySelector('.us-more'), reset=root.querySelector('.us-reset'), retry=root.querySelector('.us-retry'), main=root.querySelector('[data-main-accordion]'), chips=root.querySelector('.us-selected');
  const categories=[...root.querySelectorAll('[data-category]')].map(button=>button.dataset.category);
  const params=pageURL().searchParams, incomingStyle=params.get('us_style');
  const baseStyle=nativeStyle==='all' && params.get('us_flow')==='style' && categories.includes(incomingStyle) ? incomingStyle : nativeStyle;
  let selected=new Set(params.getAll('us_size').flatMap(value=>value.split(',')).filter(Boolean).map(normalizeSize));
  let style=categories.includes(incomingStyle)?incomingStyle:baseStyle;
  if(style==='sweatsuit')style='sweatsuits';
  let activeGroup=selected.size?sizeGroup([...selected][0]):'', data=[], scoped=[], results=[], shown=0, version=0, loaded=false;
  const styleFirst=nativeStyle!=='all' || params.get('us_flow')==='style';
  root.dataset.flow=styleFirst?'style':'size';
  if(styleFirst){root.querySelector('.us-steps').prepend(root.querySelector('.us-step-category'));root.querySelector('.us-step-category .us-step-number').textContent='1';root.querySelector('.us-step-group .us-step-number').textContent='2';root.querySelector('.us-step-size .us-step-number').textContent='3';}
  function updateURL(){
    const url=pageURL();
    if(root.dataset.themeRole==='unpublished')url.searchParams.set('preview_theme_id',root.dataset.theme);
    if(selected.size)url.searchParams.set('us_size',[...selected].join(','));else url.searchParams.delete('us_size');
    if(style!==nativeStyle)url.searchParams.set('us_style',style);else url.searchParams.delete('us_style');
    pageHistory().replaceState(null,'',url.pathname+url.search+url.hash);
  }
  function selectionUI(){
    root.querySelectorAll('[data-size]').forEach(button=>button.setAttribute('aria-pressed',String(selected.has(button.dataset.size))));
    root.querySelectorAll('[data-group-choice]').forEach(button=>button.setAttribute('aria-pressed',String(activeGroup===button.dataset.groupChoice)));
    root.querySelector('.us-group-hint').hidden=Boolean(activeGroup);
    root.querySelectorAll('[data-group]').forEach(group=>{
      const name=group.dataset.group;
      group.hidden=['regular','big'].includes(name)?name!==activeGroup:!group.querySelector('[data-size]');
      if(name===activeGroup && group.tagName==='DETAILS')group.open=true;
    });
    chips.replaceChildren();
    const categoryChip=document.createElement('span');categoryChip.className='us-category-chip';categoryChip.textContent=words.categories[style] || style;chips.append(categoryChip);
    selected.forEach(size=>{const button=document.createElement('button');button.type='button';button.dataset.removeSize=size;button.textContent=size+' ×';button.setAttribute('aria-label',words.remove+' '+size);chips.append(button);});
    reset.hidden=!selected.size && style===baseStyle;
    root.querySelectorAll('[data-category]').forEach(button=>{
      const key=button.dataset.category;
      button.setAttribute('aria-pressed',String(key===style));
      if(!loaded)return;
      const categoryProducts=data.filter(product=>styleMatches(product,key));
      const count=categoryProducts.filter(product=>!selected.size || matchingVariants(product,selected).length).length;
      button.querySelector('[data-count]').textContent=String(count);
      button.hidden=categoryProducts.length===0 && key!==style && key!=='all';
    });
    const activeCategory=root.querySelector(`[data-category="${style}"]`);
    if(activeCategory?.closest('.us-more-styles'))activeCategory.closest('.us-more-styles').open=true;
  }
  function makeCard(product,index){
    const variants=selected.size?matchingVariants(product,selected):product.variants;
    const article=document.createElement('article');article.className='us-card';article.dataset.product=product.id;
    const link=document.createElement('a');link.href=`${product.url}?variant=${variants[0].id}&us_size=${encodeURIComponent(normalizeSize(variants[0].size))}`;
    if(product.image){const img=document.createElement('img');img.src=product.image;img.alt=product.title;img.width=450;img.height=600;img.loading=index<4?'eager':'lazy';link.append(img);}
    const title=document.createElement('strong');title.textContent=product.title;link.append(title);
    const price=document.createElement('small');price.textContent=variants[0].price;link.append(price);article.append(link);
    if(selected.size){const choices=document.createElement('div');choices.className='us-variants';const seen=new Set();variants.forEach(variant=>{if(seen.has(variant.size))return;seen.add(variant.size);const a=document.createElement('a');a.href=`${product.url}?variant=${variant.id}&us_size=${encodeURIComponent(normalizeSize(variant.size))}`;a.textContent=`${words.size}: ${variant.size}`;choices.append(a);});article.append(choices);}
    return article;
  }
  function renderMore(){
    const fragment=document.createDocumentFragment();results.slice(shown,shown+24).forEach((product,i)=>fragment.append(makeCard(product,shown+i)));grid.append(fragment);shown=Math.min(shown+24,results.length);more.hidden=shown>=results.length;status.hidden=false;
    status.textContent=results.length?`${words.categories[style] || style} · ${results.length} ${words.items}${selected.size?' · '+[...selected].join(', '):''} · ${words.displaying} ${shown}`:words.empty;
    root.dataset.resultCount=String(results.length);
  }
  function updateSizes(){
    scoped=data.filter(product=>styleMatches(product,style));
    const available=new Set(scoped.flatMap(product=>product.variants.map(variant=>normalizeSize(variant.size))));
    root.querySelectorAll('[data-group]').forEach(group=>{
      const name=group.dataset.group, sizes=[...available].filter(size=>sizeGroup(size)===name), order=name==='regular'?regularOrder:bigOrder;
      sizes.sort((a,b)=>{const ai=order.indexOf(a),bi=order.indexOf(b);return(ai<0?100:ai)-(bi<0?100:bi)||a.localeCompare(b,undefined,{numeric:true});});
      const buttons=group.querySelector('.us-size-grid');buttons.replaceChildren();
      sizes.forEach(size=>{const button=document.createElement('button');button.type='button';button.dataset.size=size;button.textContent=size;button.setAttribute('aria-pressed',String(selected.has(size)));buttons.append(button);});
      group.querySelector('.us-empty-group').hidden=Boolean(sizes.length);
    });
  }
  function render(){
    if(loaded)updateSizes();
    selectionUI();
    const filtering=selected.size>0 || style!==nativeStyle || ['rugby','sweatshirts','pants-jeans'].includes(style);
    root.classList.toggle('us-filtering',filtering);
    if(collection)document.body.classList.toggle('us-size-filter-active',filtering);
    grid.replaceChildren();shown=0;more.hidden=true;status.hidden=!filtering;delete root.dataset.resultCount;
    if(!filtering){status.textContent='';return;}
    if(!loaded){status.textContent=words.loading;return;}
    results=scoped.filter(product=>!selected.size || matchingVariants(product,selected).length>0);
    renderMore();
  }
  async function refresh(){
    const current=++version;loaded=false;root.dataset.loading='true';help.textContent=words.loading;retry.hidden=true;render();
    try{data=await loadCollection(root,'all');if(current!==version)return;loaded=true;root.dataset.loading='false';help.textContent=words.help;render();}
    catch(error){if(current!==version)return;root.dataset.loading='error';console.warn('Unique Style size availability:',error.message);help.textContent=words.error;retry.hidden=false;status.textContent=words.error;grid.replaceChildren();more.hidden=true;}
  }
  root.addEventListener('click',event=>{
    const group=event.target.closest('[data-group-choice]');
    if(group){activeGroup=group.dataset.groupChoice;selectionUI();return;}
    const sizeButton=event.target.closest('[data-size],[data-remove-size]');
    if(sizeButton){const size=sizeButton.dataset.size || sizeButton.dataset.removeSize;if(selected.has(size))selected.delete(size);else selected.add(size);updateURL();render();return;}
    const category=event.target.closest('[data-category]');
    if(category){style=category.dataset.category;updateURL();render();}
  });
  reset.addEventListener('click',()=>{selected.clear();style=baseStyle==='sweatsuit'?'sweatsuits':baseStyle;updateURL();render();});
  more.addEventListener('click',renderMore);retry.addEventListener('click',refresh);
  main.addEventListener('toggle',()=>{if(main.open && !loaded && root.dataset.loading!=='true')refresh();});
  window.addEventListener('popstate',()=>{const p=pageURL().searchParams;selected=new Set(p.getAll('us_size').flatMap(value=>value.split(',')).filter(Boolean).map(normalizeSize));style=categories.includes(p.get('us_style'))?p.get('us_style'):baseStyle;render();});
  if(selected.size || style!==nativeStyle)main.open=true;
  if(main.open)refresh();else selectionUI();
}

function initNavigation(){
  document.querySelectorAll('.us-site-menu').forEach(menu=>{
    if(menu.dataset.ready)return;menu.dataset.ready='true';
    const desktop=window.matchMedia('(min-width: 960px)'), resize=()=>{menu.open=desktop.matches;};resize();desktop.addEventListener('change',resize);
    menu.querySelectorAll('.us-nav-dropdown').forEach(details=>details.addEventListener('toggle',()=>{if(details.open)menu.querySelectorAll('.us-nav-dropdown').forEach(other=>{if(other!==details)other.open=false;});}));
    menu.addEventListener('keydown',event=>{if(event.key==='Escape'){const open=menu.querySelector('.us-nav-dropdown[open]');if(open){open.open=false;open.querySelector('summary').focus();}else if(!desktop.matches){menu.open=false;menu.querySelector('summary').focus();}}});
    document.addEventListener('click',event=>{if(!menu.contains(event.target))menu.querySelectorAll('.us-nav-dropdown').forEach(details=>details.open=false);});
  });
  if(document.documentElement.hasAttribute('data-us-review-shell')){
    const width=document.querySelector('[data-review-width]'), frame=document.querySelector('iframe');
    width.addEventListener('change',()=>{frame.style.width=width.value+'px';});
  }
}

if(typeof document!=='undefined'){
  const start=()=>{document.querySelectorAll('[data-us-sbs]').forEach(init);initNavigation();initProduct();};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',start,{once:true});else start();
  document.addEventListener('shopify:section:load',start);
}

function initProduct() {
  if(!pageURL().searchParams.has('us_size'))return;
  const template=document.querySelector('[data-us-product]'), form=document.querySelector('form[action*="/cart/add"]');
  if(!template||!form||form.dataset.usSizeReady)return;
  const data=JSON.parse(template.innerHTML), original=form.querySelector('[name="id"]');
  if(!original)return;
  form.dataset.usSizeReady='true';form.classList.add('us-size-product-form');
  const label=document.createElement('label'), select=document.createElement('select');
  label.className='us-variant-choice';label.textContent=data.label;select.setAttribute('aria-label',data.label);
  data.variants.forEach(variant=>{const option=document.createElement('option');option.value=variant.id;option.textContent=variant.title+' · '+variant.price;select.append(option);});
  if(data.variants.some(variant=>String(variant.id)===original.value))select.value=original.value;
  else {const unavailable=document.createElement('option');unavailable.value='';unavailable.textContent=data.unavailable;unavailable.disabled=true;unavailable.selected=true;select.prepend(unavailable);}
  label.append(select);form.prepend(label);
  function sync(event){
    const variant=data.variants.find(variant=>String(variant.id)===select.value);
    form.querySelectorAll('[type="submit"]').forEach(button=>button.disabled=!variant);
    if(!variant){original.value='';if(event?.type==='submit')event.preventDefault();return;}
    original.value=select.value;
    const price=document.querySelector('#product-price');if(price)price.textContent=variant.price;
    const sku=document.querySelector('.variant-sku');if(sku)sku.textContent=variant.sku;
    const url=pageURL();url.searchParams.set('variant',select.value);pageHistory().replaceState(null,'',url.pathname+url.search);
  }
  select.addEventListener('change',sync);form.addEventListener('submit',sync,true);sync();
}
